import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import cotacaoService from "../../services/cotacaoService";
import CotacaoModal from "./CotacaoModal";
import Banner from "../../components/ui/Banner";

const STATUS_BADGE = {
  Rascunho: "secondary",
  EmAnalise: "info",
  Encerrada: "success",
  Cancelada: "dark",
};

const STATUS_LABEL = {
  Rascunho: "Rascunho",
  EmAnalise: "Em análise",
  Encerrada: "Encerrada",
  Cancelada: "Cancelada",
};

const fmtD = (d) => {
  try {
    const s = (d || "").split("T")[0];
    return s ? new Date(`${s}T12:00:00`).toLocaleDateString("pt-BR") : "—";
  } catch { return "—"; }
};

export default function CotacaoList({ onNavigate }) {
  const { condominioAtivo, podeEditar } = useAuth();
  const canEdit = podeEditar("cotacao");
  const [idCondo, setIdCondo] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [okMsg, setOkMsg] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroPendentes, setFiltroPendentes] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1 });
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusList, setStatusList] = useState([]);

  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  useEffect(() => {
    cotacaoService.meta().then((r) => setStatusList(r.data?.status || [])).catch(() => {});
  }, []);

  const carregar = useCallback(async () => {
    if (!idCondo) { setData([]); return; }
    setLoading(true); setError(null);
    try {
      const r = await cotacaoService.listar({
        idCondominio: idCondo,
        status: filtroStatus,
        comPendentes: filtroPendentes ? "1" : "",
        page,
        limit: 15,
      });
      setData(r.data || []);
      setPagination(r.pagination || { totalPages: 1 });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [idCondo, filtroStatus, filtroPendentes, page]);

  useEffect(() => { carregar(); }, [carregar]);

  if (!idCondo) {
    return (
      <div className="page-header">
        <h1 className="page-title">Cotação</h1>
        <p className="page-subtitle">Selecione um condomínio no topo.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header d-flex flex-wrap justify-content-between gap-2">
        <div>
          <h1 className="page-title">Cotação</h1>
          <p className="page-subtitle">
            Compare propostas, envie convites, gere pedidos e exporte o mapa comparativo
          </p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-primary btn-sm"
            onClick={() => { setEditId(null); setShowModal(true); }}>
            <i className="bi bi-plus-lg me-1" /> Nova cotação
          </button>
        )}
      </div>

      {error && <Banner tipo="danger" onClose={() => setError(null)}>{error}</Banner>}
      {okMsg && <Banner tipo="success" onClose={() => setOkMsg(null)}>{okMsg}</Banner>}

      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
        <select className="form-select form-select-sm" style={{ maxWidth: 200 }}
          value={filtroStatus} onChange={(e) => { setFiltroStatus(e.target.value); setPage(1); }}>
          <option value="">Todos os status</option>
          {statusList.map((s) => (
            <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>
          ))}
        </select>
        <label className="form-check form-check-inline small mb-0">
          <input type="checkbox" className="form-check-input"
            checked={filtroPendentes}
            onChange={(e) => { setFiltroPendentes(e.target.checked); setPage(1); }} />
          Com fornecedores pendentes
        </label>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>Nº</th><th>Título</th><th>Limite</th><th>Status</th>
                <th className="text-end">Itens</th>
                <th className="text-end">Fornec.</th>
                <th className="text-end">Resp.</th>
                <th className="text-end">Pend.</th><th />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center py-4 text-muted">Carregando…</td></tr>
              )}
              {!loading && data.map((c) => (
                <tr key={c.id}>
                  <td><code>{c.nrCotacao || c.nrcotacao}</code></td>
                  <td>{c.titulo}</td>
                  <td>{fmtD(c.dtLimite || c.dtlimite)}</td>
                  <td>
                    <span className={`badge bg-${STATUS_BADGE[c.status] || "secondary"}`}>
                      {STATUS_LABEL[c.status] || c.status}
                    </span>
                  </td>
                  <td className="text-end">{c.qtdItens ?? c.qtditens ?? 0}</td>
                  <td className="text-end">{c.qtdFornecedores ?? c.qtdfornecedores ?? 0}</td>
                  <td className="text-end">{c.qtdRespondidas ?? c.qtdrespondidas ?? 0}</td>
                  <td className="text-end">
                    {(c.qtdPendentes ?? c.qtdpendentes ?? 0) > 0 ? (
                      <span className="badge bg-warning text-dark">
                        {c.qtdPendentes ?? c.qtdpendentes}
                      </span>
                    ) : "0"}
                  </td>
                  <td className="text-end">
                    <button type="button" className="btn btn-outline-secondary btn-sm"
                      onClick={() => { setEditId(c.id); setShowModal(true); }}>
                      <i className="bi bi-pencil" />
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && !data.length && (
                <tr><td colSpan={9} className="text-center text-muted py-4">Nenhuma cotação.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <div className="d-flex justify-content-center gap-2 mt-3">
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1}
            onClick={() => setPage((x) => x - 1)}>Anterior</button>
          <span className="align-self-center small">{page} / {pagination.totalPages}</span>
          <button type="button" className="btn btn-sm btn-outline-secondary"
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((x) => x + 1)}>Próxima</button>
        </div>
      )}

      <CotacaoModal
        show={showModal}
        onClose={() => { setShowModal(false); setEditId(null); }}
        onSaved={() => { setOkMsg("Cotação salva."); carregar(); }}
        onCreated={(id) => {
          setEditId(id);
          setOkMsg("Cotação criada — registre fornecedores e respostas.");
          carregar();
        }}
        cotacaoId={editId}
        idCondominio={idCondo}
        canEdit={canEdit}
        saving={saving}
        setSaving={setSaving}
        onNavigate={onNavigate}
      />
    </div>
  );
}
